import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  RadioGroup,
  Tabs,
  Tab
} from '@mui/material';
import { Close, PictureAsPdf, Lock, VpnKey, CreditCard } from '@mui/icons-material';
import tokens from '../../theme/tokens';
import { useLanguage } from '../../localization';
import { PAYMENT_PRODUCTS, formatPrice, getProductFeatures } from '../../config/paymentProducts';
import PaymentOption from './PaymentOption';
import CodeInputCard from './CodeInputCard';

// Fallback product configurations if translations fail
const getFallbackProductConfig = (productId, product) => {
  const fallbacks = {
    pdf_one_time: {
      name: 'One-Time PDF Download',
      description: 'Single PDF export',
      features: getProductFeatures(product)
    }
  };
  
  return fallbacks[productId] || {
    name: product.name,
    description: product.description,
    features: getProductFeatures(product)
  };
};

const PaymentContent = ({ 
  selectedProductId, 
  onProductSelect, 
  onProceedToCheckout, 
  onCancel, 
  isProcessing,
  onCodeSubmit,
  codeError,
  activeTab,
  onTabChange,
  isMobile = false
}) => {
  const { t } = useLanguage();

  return (
    <>
      <DialogContent sx={{ 
        p: 0, 
        maxHeight: { xs: 'calc(100vh - 180px)', sm: '70vh' }, 
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
          <Tabs 
            value={activeTab} 
            onChange={onTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: tokens.typography.fontWeights.medium,
                py: { xs: 1, sm: 1.5 },
                minHeight: { xs: 44, sm: 48 }
              }
            }}
          >
            <Tab 
              icon={<VpnKey sx={{ fontSize: 18 }} />} 
              label={t('payment.tabs.existingCustomer')} 
              iconPosition="start"
            />
            <Tab 
              icon={<CreditCard sx={{ fontSize: 18 }} />} 
              label={t('payment.tabs.newPurchase')} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 ? (
          // Code Input Tab
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 } }}>
            <CodeInputCard 
              onCodeSubmit={onCodeSubmit}
              isProcessing={isProcessing}
              error={codeError}
              translations={t}
            />
          </Box>
        ) : (
          // Payment Options Tab
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                textAlign: 'center'
              }}
            >
              {t('payment.modal.chooseOption')}
            </Typography>
            
            {/* Payment Options */}
            <RadioGroup value={selectedProductId || ''} onChange={(e) => onProductSelect(e.target.value)}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 1.5, sm: 2 }
              }}>
                {Object.values(PAYMENT_PRODUCTS).map((product, index) => {
                  // Access nested translation keys individually
                  const productConfig = {
                    name: t(`payment.products.${product.id}.name`),
                    description: t(`payment.products.${product.id}.description`),
                    features: []
                  };
                  
                  // Build features array dynamically
                  let featureIndex = 0;
                  while (featureIndex < 10) { // Max 10 features
                    const featureKey = `payment.products.${product.id}.features.${featureIndex}`;
                    const feature = t(featureKey);
                    if (feature && !feature.startsWith('payment.products.')) {
                      productConfig.features.push(feature);
                      featureIndex++;
                    } else {
                      break;
                    }
                  }
                  
                  // Fallback if translations don't work
                  if (!productConfig.name || productConfig.name.startsWith('payment.products.')) {
                    const fallbackConfig = getFallbackProductConfig(product.id, product);
                    Object.assign(productConfig, fallbackConfig);
                  }
                  
                  // Determine if this is the popular option (week pass)
                  const isPopular = product.id === 'pdf_week';
                  
                  return (
                    <React.Fragment key={product.id}>
                      <PaymentOption
                        product={product}
                        productConfig={productConfig}
                        isSelected={selectedProductId === product.id}
                        onSelect={isMobile ? onProceedToCheckout : onProductSelect}
                        translations={t}
                        isPopular={isPopular}
                        isMobile={isMobile}
                        isProcessing={isProcessing}
                      />
                      
                      {/* Show explanatory text after the first (one-time) option */}
                      {index === 0 && (
                        <Box
                          sx={{
                            backgroundColor: `${tokens.colors.primary.main}06`,
                            border: `1px solid ${tokens.colors.primary.main}15`,
                            borderRadius: tokens.radius.base,
                            p: { xs: 1.5, sm: 2 },
                            mx: { xs: 0, sm: 0.5 }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: tokens.colors.text.secondary,
                              fontSize: { xs: '0.8rem', sm: '0.85rem' },
                              lineHeight: 1.5,
                              textAlign: 'center',
                              fontWeight: tokens.typography.fontWeights.normal
                            }}
                          >
                            {t('payment.modal.explanatoryText')}
                          </Typography>
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </RadioGroup>
          </Box>
        )}

        {/* Security Notice - only show when product is selected or code tab */}
        {(selectedProductId || activeTab === 0) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: { xs: 1.5, sm: 2 },
              backgroundColor: `${tokens.colors.success.main}10`,
              borderRadius: tokens.radius.base,
              border: `1px solid ${tokens.colors.success.main}30`,
              mt: { xs: 2, sm: 3 },
              mx: { xs: 0.5, sm: 1 }
            }}
          >
            <Lock sx={{ 
              color: tokens.colors.success.main, 
              mr: 1, 
              fontSize: { xs: 14, sm: 16 },
              flexShrink: 0
            }} />
            <Typography
              variant="body2"
              sx={{
                color: tokens.colors.success.dark,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                lineHeight: 1.3
              }}
            >
              {t('payment.modal.securityNotice')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, sm: 3 }, 
        gap: { xs: 1, sm: 1.5 },
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        display: { xs: activeTab === 1 && isMobile ? 'none' : 'flex', sm: 'flex' }
      }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={isProcessing}
          fullWidth={true}
          sx={{
            textTransform: 'none',
            borderColor: tokens.colors.borders.light,
            color: tokens.colors.text.secondary,
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1.2, sm: 1 },
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              borderColor: tokens.colors.text.secondary,
              backgroundColor: 'transparent'
            }
          }}
        >
          {t('payment.modal.cancel')}
        </Button>
        
        {activeTab === 1 && (
          <Button
            onClick={onProceedToCheckout}
            variant="contained"
            disabled={isProcessing || !selectedProductId}
            fullWidth={true}
            sx={{
              backgroundColor: tokens.colors.primary.main,
              color: 'white',
              textTransform: 'none',
              fontWeight: tokens.typography.fontWeights.medium,
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1 },
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: tokens.colors.primary.dark
              },
              '&:disabled': {
                backgroundColor: tokens.colors.text.disabled
              }
            }}
          >
            {isProcessing ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                {t('payment.modal.redirecting')}
              </Box>
            ) : (
              t('payment.modal.proceedButton')
            )}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

const PaymentModal = ({ open, onClose, onSuccess, stripePromise, selectedSections = [] }) => {
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [paymentWindow, setPaymentWindow] = useState(null);
  const [activeTab, setActiveTab] = useState(1); // Start with payment options tab
  const { t, currentLanguage } = useLanguage();
  
  // Detect mobile screen size
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listen for messages from the payment window
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: Only accept messages from our own domain
      if (event.origin !== window.location.origin) {
        console.log('Ignoring message from different origin:', event.origin);
        return;
      }

      console.log('Received message:', event.data);

      if (event.data.type === 'stripe_checkout_success') {
        console.log('Payment successful - received message from success page');
        if (paymentWindow) {
          paymentWindow.close();
          setPaymentWindow(null);
        }
        setIsProcessing(false);
        onClose();
        
        // Trigger PDF export immediately since app state is preserved
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 500);
      } else if (event.data.type === 'stripe_checkout_cancel') {
        console.log('Payment cancelled - received message from cancel page');
        if (paymentWindow) {
          paymentWindow.close();
          setPaymentWindow(null);
        }
        setIsProcessing(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Check if payment window was closed manually
    const checkWindowClosed = setInterval(() => {
      if (paymentWindow && paymentWindow.closed) {
        console.log('Payment window was closed manually');
        setPaymentWindow(null);
        setIsProcessing(false);
        clearInterval(checkWindowClosed);
      }
    }, 1000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(checkWindowClosed);
    };
  }, [paymentWindow, onClose, onSuccess]);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedProductId(null);
      setError(null);
      setCodeError(null);
      setActiveTab(1); // Default to payment options
    }
  }, [open]);

  // Don't render if Stripe is not configured
  if (!stripePromise) {
    return null;
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
    setCodeError(null);
    setSelectedProductId(null);
  };

  const handleCodeSubmit = async (code) => {
    setIsProcessing(true);
    setCodeError(null);
    
    try {
      // TODO: Implement code validation logic here
      const response = await fetch('/api/validate-pass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: code }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || t('payment.codeInput.errors.generic'));
      }
      
      // Successful redemption – trigger PDF export
      // (Backend streams PDF, so user may receive browser download automatically)
      // Close modal and call onSuccess to generate PDF from current state as fallback
      
      // If successful, trigger PDF download
      onClose();
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 500);
      
    } catch (err) {
      setCodeError(err.message || t('payment.codeInput.errors.generic'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProceedToCheckout = async (productId = selectedProductId) => {
    const targetProductId = productId || selectedProductId;
    
    if (!targetProductId) {
      setError(t('payment.errors.noProductSelected'));
      return;
    }

    // On mobile, set the selected product if it was passed directly
    if (isMobile && productId && productId !== selectedProductId) {
      setSelectedProductId(productId);
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create success/cancel URLs that will communicate back to parent window
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment-success.html`;
      const cancelUrl = `${baseUrl}/payment-cancel.html`;

      console.log('Creating checkout session for new window with product:', selectedProductId);

      // Create checkout session with selected product and language
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          successUrl,
          cancelUrl,
          productId: targetProductId,
          language: currentLanguage, // Pass the user's language preference
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('API Response:', text);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const { url, error: serverError } = data;

      if (serverError) {
        throw new Error(serverError);
      }

      // Open Stripe Checkout in a new window
      const newWindow = window.open(
        url,
        'stripe-checkout',
        'width=600,height=700,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
      );

      if (!newWindow) {
        throw new Error('Unable to open payment window. Please allow popups and try again.');
      }

      setPaymentWindow(newWindow);

    } catch (err) {
      setError(err.message || t('payment.errors.failedToCreate'));
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={false}
      PaperProps={{
        sx: {
          borderRadius: { xs: tokens.radius.lg, sm: tokens.radius.xl },
          maxWidth: { xs: '100vw', sm: 580 },
          maxHeight: { xs: '100vh', sm: '95vh' },
          m: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
          height: { xs: '100%', sm: 'auto' }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: { xs: 1, sm: 1.5 },
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: `${tokens.colors.primary.main}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <PictureAsPdf sx={{ color: tokens.colors.primary.main, fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}
            >
              {t('payment.modal.title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: tokens.colors.primary.main,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                fontWeight: tokens.typography.fontWeights.medium
              }}
            >
              {t('payment.modal.subtitle')}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isProcessing}
          sx={{
            color: tokens.colors.text.secondary,
            '&:hover': {
              backgroundColor: `${tokens.colors.text.secondary}10`
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ mx: { xs: 2, sm: 3 } }} />

      <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: tokens.radius.base,
              fontSize: '0.85rem'
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        <PaymentContent
          selectedProductId={selectedProductId}
          onProductSelect={setSelectedProductId}
          onProceedToCheckout={handleProceedToCheckout}
          onCancel={onClose}
          isProcessing={isProcessing}
          onCodeSubmit={handleCodeSubmit}
          codeError={codeError}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isMobile={isMobile}
        />
      </Box>
    </Dialog>
  );
};

export default PaymentModal; 